export enum EFurnitureStyle {
  RIMUOVI_ARREDI = 'rimuovi-arredi',
  TOSCANO = 'toscano',
  MARITTIMO = 'marittimo',
  MINIMALISTA = 'minimalista',
  INDUSTRIALE = 'industriale',
  SCANDINAVO = 'scandinavo',
  JAPANDI = 'japandi',
  BOHEMIEN = 'bohemien',
  MID_CENTURY = 'mid-century',
  SHABBY_CHIC = 'shabby-chic',
  RUSTICO_MODERNO = 'rustico-moderno',
  CONTEMPORANEO = 'contemporaneo',
}

export interface FurnitureStyleOption {
  value: EFurnitureStyle;
  label: string;
  description: string;
  promptLabel: string;
  imageSrc: string;
}

export const FURNITURE_STYLE_OPTIONS: FurnitureStyleOption[] = [
  {
    value: EFurnitureStyle.RIMUOVI_ARREDI,
    label: 'Rimuovi arredi ed oggetti',
    description:
      'Rimuove completamente arredi, oggetti e accessori non strutturali per lasciare un ambiente vuoto, pulito e neutro.',
    promptLabel: 'empty unfurnished interior shell',
    imageSrc: '/interior-styles/rimuovi-arredi.jpg',
  },
  {
    value: EFurnitureStyle.CONTEMPORANEO,
    label: 'Contemporaneo',
    description:
      'Riflette le tendenze del momento, con un uso bilanciato di linee curve e rette, materiali innovativi e una palette di colori spesso basata su contrasti (come il bianco e nero).',
    promptLabel: 'Contemporaneo',
    imageSrc: '/interior-styles/contemporaneo.jpg',
  },
  {
    value: EFurnitureStyle.MINIMALISTA,
    label: 'Minimalista',
    description:
      'Basato sul concetto "less is more", predilige linee pulite, spazi aperti e una palette di colori neutri. Ogni oggetto ha una funzione precisa e l\'ordine è fondamentale.',
    promptLabel: 'Minimalista',
    imageSrc: '/interior-styles/minimalista.jpg',
  },
  {
    value: EFurnitureStyle.SCANDINAVO,
    label: 'Scandinavo',
    description:
      'Focus sulla funzionalità, la luminosità e il comfort (il concetto di hygge). Usa molto legno chiaro, forme semplici e colori neutri con accenni pastello.',
    promptLabel: 'Scandinavo',
    imageSrc: '/interior-styles/scandinavo.jpg',
  },
  {
    value: EFurnitureStyle.MID_CENTURY,
    label: 'Moderno Mid-Century',
    description:
      "Riprende il design degli anni '50 e '60, con mobili dalle forme organiche, gambe a spillo e un perfetto equilibrio tra estetica e praticità.",
    promptLabel: 'Moderno Mid-Century',
    imageSrc: '/interior-styles/moderno.jpg',
  },
  {
    value: EFurnitureStyle.TOSCANO,
    label: 'Stile Toscano',
    description:
      "Caratterizzato da un'atmosfera calda e rustica, utilizza materiali naturali come il cotto per i pavimenti, la pietra a vista, travi in legno sul soffitto e mobili in legno massiccio. I colori richiamano la terra (ocra, terra di Siena, verde oliva).",
    promptLabel: 'Stile Toscano',
    imageSrc: '/interior-styles/toscano.jpg',
  },
  {
    value: EFurnitureStyle.RUSTICO_MODERNO,
    label: 'Rustico Moderno',
    description:
      'Unisce il calore dei materiali grezzi (legno, pietra) con arredi moderni e linee più sottili per un risultato accogliente ma sofisticato.',
    promptLabel: 'Rustico Moderno',
    imageSrc: '/interior-styles/rustico-moderno.jpg',
  },
  {
    value: EFurnitureStyle.MARITTIMO,
    label: 'Stile Marittimo (o Coastal)',
    description:
      'Si ispira alle case al mare, puntando su molta luce naturale, colori come il bianco, il blu navy e le tonalità della sabbia. Utilizza tessuti leggeri come il lino, legni chiari o "consumati" dal sale e decorazioni a tema nautico.',
    promptLabel: 'Stile Marittimo (o Coastal)',
    imageSrc: '/interior-styles/marittimo.jpg',
  },
  {
    value: EFurnitureStyle.SHABBY_CHIC,
    label: 'Shabby Chic',
    description:
      'Uno stile romantico ed elegante che predilige mobili d\'epoca con un aspetto "vissuto" o decapato, colori pastello e fantasie floreali.',
    promptLabel: 'Shabby Chic',
    imageSrc: '/interior-styles/shabby-chic.jpg',
  },
  {
    value: EFurnitureStyle.JAPANDI,
    label: 'Japandi',
    description:
      'Fonde il minimalismo giapponese con il calore nordico: palette naturali, linee essenziali, legni chiari e ambienti ariosi con pochi elementi ben scelti.',
    promptLabel: 'Japandi',
    imageSrc: '/interior-styles/japandi.jpg',
  },
  {
    value: EFurnitureStyle.BOHEMIEN,
    label: 'Bohemien (Boho Chic)',
    description:
      'Uno stile eclettico e libero che mescola colori vivaci, pattern diversi, piante da interno e oggetti provenienti da varie culture. È perfetto per chi ama un ambiente personale e creativo.',
    promptLabel: 'Bohemien (Boho Chic)',
    imageSrc: '/interior-styles/bohemien.jpg',
  },
  {
    value: EFurnitureStyle.INDUSTRIALE,
    label: 'Industriale',
    description:
      'Ispirato ai loft newyorkesi ricavati da vecchie fabbriche, mette in mostra elementi strutturali come mattoni a vista, tubature, cemento e metallo, spesso abbinati a legno grezzo.',
    promptLabel: 'Industriale',
    imageSrc: '/interior-styles/industriale.jpg',
  },
];

export const FURNITURE_STYLE_PROMPT_LABELS: Record<EFurnitureStyle, string> =
  FURNITURE_STYLE_OPTIONS.reduce(
    (acc, option) => ({
      ...acc,
      [option.value]: option.promptLabel,
    }),
    {} as Record<EFurnitureStyle, string>
  );
export enum ELuxuryLevel {
  BASSO = 'Essenziale',
  MEDIO = 'Bilanciato',
  ALTO = 'Premium',
}

export interface LuxuryLevelOption {
  value: ELuxuryLevel;
  label: string;
  description: string;
}

export const LUXURY_LEVEL_OPTIONS: LuxuryLevelOption[] = [
  {
    value: ELuxuryLevel.BASSO,
    label: 'Essenziale',
    description: 'Soluzione pulita e sobria, attenta al budget.',
  },
  {
    value: ELuxuryLevel.MEDIO,
    label: 'Bilanciato',
    description: 'Materiali più ricchi con equilibrio tra resa e costo.',
  },
  {
    value: ELuxuryLevel.ALTO,
    label: 'Premium',
    description:
      'Finiture e dettagli ad alto impatto per listing di fascia alta.',
  },
];

export interface WizardAnswers {
  furnitureStyle: EFurnitureStyle;
  luxuryLevel: ELuxuryLevel;
  removeFurniture?: boolean;
  removePersonalEffects?: boolean;
  improveImages?: boolean;
}

export type ProcessingStatus = 'pending' | 'processing' | 'done' | 'error';

export enum ERoomType {
  LIVING = 'living',
  CUCINA = 'cucina',
  CAMERA = 'camera',
  BAGNO = 'bagno',
  STUDIO = 'studio',
  INGRESSO = 'ingresso',
  BALCONE = 'balcone',
}

export interface RoomTypeOption {
  value: ERoomType;
  label: string;
  promptLabel: string;
}

export const ROOM_TYPE_OPTIONS: RoomTypeOption[] = [
  { value: ERoomType.LIVING, label: 'Soggiorno / Living', promptLabel: 'living room' },
  { value: ERoomType.CUCINA, label: 'Cucina', promptLabel: 'kitchen' },
  { value: ERoomType.CAMERA, label: 'Camera da letto', promptLabel: 'bedroom' },
  { value: ERoomType.BAGNO, label: 'Bagno', promptLabel: 'bathroom' },
  { value: ERoomType.STUDIO, label: 'Studio / Ufficio', promptLabel: 'home office / study room' },
  { value: ERoomType.INGRESSO, label: 'Ingresso / Corridoio', promptLabel: 'entrance hall / hallway' },
  { value: ERoomType.BALCONE, label: 'Balcone / Terrazzo', promptLabel: 'balcony / terrace' },
];

export const ROOM_TYPE_PROMPT_LABELS: Record<ERoomType, string> =
  ROOM_TYPE_OPTIONS.reduce(
    (acc, option) => ({ ...acc, [option.value]: option.promptLabel }),
    {} as Record<ERoomType, string>
  );

export interface RoomOptionDef {
  id: string;
  label: string;
  promptInstruction: string;
  /**
   * Opzioni con lo stesso exclusiveGroup sono mutuamente esclusive e vengono
   * renderizzate come select (label in EXCLUSIVE_GROUP_LABELS); le opzioni
   * senza gruppo sono checkbox indipendenti.
   */
  exclusiveGroup?: string;
}

export const EXCLUSIVE_GROUP_LABELS: Record<string, string> = {
  'living-sofa': 'Configurazione divano',
  'living-density': 'Densità allestimento',
  'cucina-mode': 'Cucina esistente',
  'camera-bed': 'Tipo letto',
  'bagno-washing': 'Zona lavaggio',
  'bagno-tiles': 'Rivestimenti',
  'bagno-tapware': 'Finitura rubinetteria',
  'studio-desk': 'Postazione',
  'balcone-use': 'Uso prevalente',
};

export const ROOM_OPTIONS: Record<ERoomType, RoomOptionDef[]> = {
  [ERoomType.LIVING]: [
    {
      id: 'living-sofa-linear',
      label: 'Lineare',
      promptInstruction: 'Use a linear (straight) sofa as the main seating.',
      exclusiveGroup: 'living-sofa',
    },
    {
      id: 'living-sofa-corner',
      label: 'Angolare (L)',
      promptInstruction:
        'Use a corner (L-shaped) sectional sofa as the main seating.',
      exclusiveGroup: 'living-sofa',
    },
    {
      id: 'living-sofa-facing',
      label: 'Due divani contrapposti',
      promptInstruction:
        'Use two sofas facing each other as the main seating arrangement.',
      exclusiveGroup: 'living-sofa',
    },
    {
      id: 'living-sofa-armchairs',
      label: 'Divano + poltrone',
      promptInstruction:
        'Use a sofa paired with a set of matching armchairs as the main seating arrangement.',
      exclusiveGroup: 'living-sofa',
    },
    {
      id: 'living-density-minimal',
      label: 'Essenziale e arioso',
      promptInstruction:
        'Stage the room with an essential, airy furniture layout: fewer pieces, generous open space, minimal decor.',
      exclusiveGroup: 'living-density',
    },
    {
      id: 'living-density-full',
      label: 'Completo e curato',
      promptInstruction:
        'Stage the room with a complete, richly curated furniture layout: full seating arrangement, layered decor, styled surfaces.',
      exclusiveGroup: 'living-density',
    },
    {
      id: 'living-dining-area',
      label: 'Zona pranzo con tavolo e sedie',
      promptInstruction:
        'ADD a dining area with table and chairs in the living room.',
    },
    {
      id: 'living-tv',
      label: 'TV a parete con mobile basso',
      promptInstruction:
        'ADD a wall-mounted TV with a low media cabinet underneath.',
    },
    {
      id: 'living-bookcase',
      label: 'Libreria / parete attrezzata',
      promptInstruction: 'ADD a bookcase or a styled shelving wall unit.',
    },
    {
      id: 'living-rug',
      label: 'Tappeto grande zona divano',
      promptInstruction: 'ADD a large area rug anchoring the seating zone.',
    },
    {
      id: 'living-reading',
      label: 'Angolo lettura (poltrona + lampada)',
      promptInstruction:
        'ADD a reading corner with an armchair and a floor lamp.',
    },
    {
      id: 'living-lighting',
      label: 'Lampadario di design',
      promptInstruction:
        'ADD a designer ceiling light fixture (chandelier or statement pendant).',
    },
    {
      id: 'living-curtains',
      label: 'Tende e tendaggi',
      promptInstruction:
        'ADD elegant curtains or drapes on the existing windows.',
    },
    {
      id: 'living-art',
      label: 'Quadri e arte alle pareti',
      promptInstruction: 'ADD tasteful wall art and framed pictures.',
    },
    {
      id: 'living-plants',
      label: 'Piante decorative',
      promptInstruction: 'ADD decorative indoor plants.',
    },
  ],
  [ERoomType.CUCINA]: [
    {
      id: 'cucina-keep',
      label: "Mantieni com'è",
      promptInstruction:
        'KEEP the existing kitchen cabinets, appliances, and finishes exactly as they are.',
      exclusiveGroup: 'cucina-mode',
    },
    {
      id: 'cucina-replace',
      label: 'Sostituisci (stessa disposizione)',
      promptInstruction:
        'REPLACE the existing kitchen cabinets and finishes with new ones matching the selected style, keeping the kitchen in the exact same position and layout.',
      exclusiveGroup: 'cucina-mode',
    },
    {
      id: 'cucina-dining-table',
      label: 'Tavolo da pranzo con sedie',
      promptInstruction: 'ADD a dining table with chairs.',
    },
    {
      id: 'cucina-island',
      label: 'Isola / penisola con sgabelli',
      promptInstruction:
        'ADD a kitchen island or peninsula with stools if the space allows it.',
    },
    {
      id: 'cucina-pendants',
      label: 'Sospensioni sopra tavolo/isola',
      promptInstruction:
        'ADD pendant lighting above the dining table or island.',
    },
    {
      id: 'cucina-appliances',
      label: 'Elettrodomestici a vista premium',
      promptInstruction:
        'Show premium built-in or stainless-steel appliances.',
    },
    {
      id: 'cucina-backsplash',
      label: 'Paraschizzi rinnovato',
      promptInstruction:
        'Refresh the backsplash with a modern premium finish — surface only, same position and size.',
    },
    {
      id: 'cucina-styling',
      label: 'Styling piano di lavoro',
      promptInstruction:
        'ADD worktop styling: cutting boards, aromatic plants, oil bottles, and tasteful kitchen accessories.',
    },
  ],
  [ERoomType.CAMERA]: [
    {
      id: 'camera-letto-matrimoniale',
      label: 'Matrimoniale',
      promptInstruction:
        'Furnish as a master bedroom with a double/queen bed.',
      exclusiveGroup: 'camera-bed',
    },
    {
      id: 'camera-letto-singolo',
      label: 'Singolo',
      promptInstruction: 'Furnish as a single bedroom with one single bed.',
      exclusiveGroup: 'camera-bed',
    },
    {
      id: 'camera-letto-due-singoli',
      label: 'Due letti singoli',
      promptInstruction: 'Furnish with two separate single beds.',
      exclusiveGroup: 'camera-bed',
    },
    {
      id: 'camera-letto-castello',
      label: 'A castello (cameretta)',
      promptInstruction: "Furnish as a kids' room with a bunk bed.",
      exclusiveGroup: 'camera-bed',
    },
    {
      id: 'camera-testiera',
      label: 'Testiera imbottita di design',
      promptInstruction:
        'ADD a designer upholstered headboard behind the bed.',
    },
    {
      id: 'camera-armadio',
      label: 'Armadio',
      promptInstruction: 'ADD a wardrobe.',
    },
    {
      id: 'camera-comodini',
      label: 'Comodini con lampade abbinate',
      promptInstruction:
        'ADD matching bedside tables with table lamps on both sides of the bed.',
    },
    {
      id: 'camera-panca',
      label: 'Panca fondoletto',
      promptInstruction: 'ADD a bench at the foot of the bed.',
    },
    {
      id: 'camera-scrivania',
      label: 'Scrivania / angolo studio',
      promptInstruction: 'ADD a desk or study corner.',
    },
    {
      id: 'camera-poltrona',
      label: 'Poltroncina lettura',
      promptInstruction: 'ADD a small reading armchair.',
    },
    {
      id: 'camera-tappeto',
      label: 'Tappeto ai lati del letto',
      promptInstruction: 'ADD a soft rug along the sides of the bed.',
    },
    {
      id: 'camera-specchio',
      label: 'Specchio da terra',
      promptInstruction: 'ADD a full-length floor mirror.',
    },
    {
      id: 'camera-tende',
      label: 'Tende oscuranti',
      promptInstruction: 'ADD blackout curtains on the windows.',
    },
    {
      id: 'camera-quadri',
      label: 'Quadri sopra la testiera',
      promptInstruction: 'ADD framed artwork above the headboard.',
    },
  ],
  [ERoomType.BAGNO]: [
    {
      id: 'bagno-lavaggio-mantieni',
      label: "Valorizza l'esistente",
      promptInstruction:
        'KEEP the existing washing area (shower or bathtub) in its current type and position, but make it look premium: pristine glass, renewed surfaces, polished fittings.',
      exclusiveGroup: 'bagno-washing',
    },
    {
      id: 'bagno-lavaggio-doccia',
      label: 'Doccia in vetro premium',
      promptInstruction:
        'The washing area must be a premium walk-in glass shower. If the existing fixture is already a shower, upgrade it in place. If the existing fixture is a bathtub, replace it with the glass shower in the exact same position — this specific fixture swap is explicitly authorized as an exception to the structural invariants; every other structural and plumbing element must remain unchanged.',
      exclusiveGroup: 'bagno-washing',
    },
    {
      id: 'bagno-lavaggio-vasca',
      label: 'Vasca freestanding',
      promptInstruction:
        'The washing area must be an elegant freestanding bathtub. If the existing fixture is already a bathtub, upgrade it in place. If the existing fixture is a shower, replace it with the bathtub in the exact same position — this specific fixture swap is explicitly authorized as an exception to the structural invariants; every other structural and plumbing element must remain unchanged.',
      exclusiveGroup: 'bagno-washing',
    },
    {
      id: 'bagno-rivestimenti-mantieni',
      label: 'Mantieni',
      promptInstruction:
        'KEEP the existing wall and floor tiles exactly as they are.',
      exclusiveGroup: 'bagno-tiles',
    },
    {
      id: 'bagno-rivestimenti-sostituisci',
      label: 'Sostituisci con finiture moderne',
      promptInstruction:
        'REPLACE the wall and floor tile surfaces with modern premium finishes — surface only, preserving the exact room geometry.',
      exclusiveGroup: 'bagno-tiles',
    },
    {
      id: 'bagno-rubinetteria-cromo',
      label: 'Cromo lucido',
      promptInstruction: 'Use polished chrome tapware and fittings.',
      exclusiveGroup: 'bagno-tapware',
    },
    {
      id: 'bagno-rubinetteria-nero',
      label: 'Nero opaco',
      promptInstruction: 'Use matte black tapware and fittings.',
      exclusiveGroup: 'bagno-tapware',
    },
    {
      id: 'bagno-rubinetteria-ottone',
      label: 'Ottone spazzolato',
      promptInstruction: 'Use brushed brass tapware and fittings.',
      exclusiveGroup: 'bagno-tapware',
    },
    {
      id: 'bagno-vanity',
      label: 'Mobile lavabo sospeso',
      promptInstruction:
        'ADD a wall-mounted floating vanity unit under the sink.',
    },
    {
      id: 'bagno-mirror',
      label: 'Specchio retroilluminato',
      promptInstruction: 'ADD a backlit LED mirror above the sink.',
    },
    {
      id: 'bagno-textiles',
      label: 'Tessili coordinati',
      promptInstruction: 'ADD coordinated towels and a bath mat.',
    },
    {
      id: 'bagno-accessories',
      label: 'Accessori coordinati',
      promptInstruction:
        'ADD coordinated bathroom accessories (dispensers, towel rails).',
    },
    {
      id: 'bagno-plants',
      label: 'Piante da bagno',
      promptInstruction: 'ADD humidity-loving bathroom plants.',
    },
  ],
  [ERoomType.STUDIO]: [
    {
      id: 'studio-postazione-singola',
      label: 'Singola',
      promptInstruction:
        'Furnish with a single workstation: one desk and one ergonomic chair.',
      exclusiveGroup: 'studio-desk',
    },
    {
      id: 'studio-postazione-doppia',
      label: 'Doppia',
      promptInstruction:
        'Furnish with a double workstation: two desks (or one large shared desk) and two chairs.',
      exclusiveGroup: 'studio-desk',
    },
    {
      id: 'studio-bookcase',
      label: 'Libreria a parete',
      promptInstruction: 'ADD a wall bookcase.',
    },
    {
      id: 'studio-relax',
      label: 'Angolo relax',
      promptInstruction:
        'ADD a relax corner with an armchair or a small sofa.',
    },
    {
      id: 'studio-lighting',
      label: 'Illuminazione da lavoro',
      promptInstruction:
        'ADD task lighting: a desk lamp and warm ambient lighting.',
    },
    {
      id: 'studio-rug',
      label: 'Tappeto',
      promptInstruction: 'ADD a rug under the desk area.',
    },
    {
      id: 'studio-art',
      label: 'Quadri / decorazione pareti',
      promptInstruction: 'ADD framed artwork or tasteful wall decor.',
    },
    {
      id: 'studio-plants',
      label: 'Piante',
      promptInstruction: 'ADD indoor plants.',
    },
  ],
  [ERoomType.INGRESSO]: [
    {
      id: 'ingresso-console',
      label: 'Consolle / mobile ingresso',
      promptInstruction: 'ADD an entryway console or storage cabinet.',
    },
    {
      id: 'ingresso-bench',
      label: 'Panca con scarpiera',
      promptInstruction: 'ADD an entryway bench with shoe storage.',
    },
    {
      id: 'ingresso-mirror',
      label: 'Specchio grande',
      promptInstruction: 'ADD a large mirror.',
    },
    {
      id: 'ingresso-coatrack',
      label: 'Appendiabiti',
      promptInstruction: 'ADD a coat rack or wall hooks.',
    },
    {
      id: 'ingresso-runner',
      label: 'Runner / passatoia',
      promptInstruction: 'ADD a runner rug along the hallway.',
    },
    {
      id: 'ingresso-gallery',
      label: 'Gallery wall (quadri)',
      promptInstruction: 'ADD a gallery wall with framed pictures.',
    },
    {
      id: 'ingresso-lighting',
      label: 'Illuminazione accogliente',
      promptInstruction: 'ADD warm, welcoming lighting.',
    },
  ],
  [ERoomType.BALCONE]: [
    {
      id: 'balcone-uso-pranzo',
      label: 'Zona pranzo esterna',
      promptInstruction:
        'Stage as an outdoor dining area with a table and chairs.',
      exclusiveGroup: 'balcone-use',
    },
    {
      id: 'balcone-uso-relax',
      label: 'Zona relax lounge',
      promptInstruction:
        'Stage as an outdoor relax lounge with comfortable seating.',
      exclusiveGroup: 'balcone-use',
    },
    {
      id: 'balcone-plants',
      label: 'Piante e fioriere',
      promptInstruction: 'ADD outdoor plants and planters.',
    },
    {
      id: 'balcone-lighting',
      label: 'Illuminazione esterna',
      promptInstruction: 'ADD outdoor lighting: string lights or lanterns.',
    },
    {
      id: 'balcone-rug',
      label: 'Tappeto outdoor',
      promptInstruction: 'ADD an outdoor rug.',
    },
    {
      id: 'balcone-decking',
      label: 'Pavimento effetto decking',
      promptInstruction:
        'Refresh the floor with a wood-decking-effect surface — surface texture only, same geometry and area.',
    },
    {
      id: 'balcone-shade',
      label: 'Ombrellone / vela ombreggiante',
      promptInstruction: 'ADD a parasol or shade sail.',
    },
  ],
};

export interface PhotoGenerationConfig {
  roomType: ERoomType | null;
  selectedOptionIds: string[];
  customPrompt: string;
  allowStructuralChanges: boolean;
}

export function createDefaultPhotoConfig(): PhotoGenerationConfig {
  return {
    roomType: null,
    selectedOptionIds: [],
    customPrompt: '',
    allowStructuralChanges: false,
  };
}

export interface ProcessedImage {
  index: number;
  originalFile: File;
  originalUrl: string;
  aiFile: File | null;
  aiUrl: string | null;
  prompt: string;
  status: ProcessingStatus;
  errorMessage?: string;
  config: PhotoGenerationConfig;
}

export type AbPreviewMode = 'slider' | 'side-by-side' | 'tap';
